import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiDateFormats, noddiFormatDate, parseDate } from 'noddi-util';
import { useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import { createEnumParam, DateParam, StringParam, useQueryParams } from 'use-query-params';

import type { SeasonDateRangeMode, SeasonDateRangeWindow } from '../SeasonDateRange/types';
import { nextForecastPeriodAction } from './forecastPeriod';

const periodParams = {
  'period-mode': createEnumParam(['season', 'custom'] as const),
  'period-season': StringParam,
  'from-date': DateParam,
  'to-date': DateParam
};
const replaceIn = { updateType: 'replaceIn' } as const;

export const useForecastPeriod = () => {
  const [period, setPeriod] = useQueryParams(periodParams, replaceIn);
  const { 'period-mode': periodMode, 'period-season': periodSeason, 'from-date': from, 'to-date': to } = period;
  const {
    data: seasons,
    error: seasonsError,
    refetch: refetchSeasons
  } = noddiAsync.useGet({ type: URLKeys.getRecallSeasons });
  const {
    data: currentSeason,
    error: currentSeasonError,
    refetch: refetchCurrentSeason
  } = noddiAsync.useGet({ type: URLKeys.getRecallSeasonCurrent });

  const selectedSeason = seasons?.find((season) => season.name === periodSeason);
  const dateMode: SeasonDateRangeMode =
    periodMode === 'custom' || (periodMode == null && (from || to)) ? 'custom' : 'season';
  const activeSeason =
    dateMode === 'season'
      ? (selectedSeason ?? (periodSeason == null || periodSeason === currentSeason?.name ? currentSeason : undefined))
      : undefined;
  const fromDate = from ? noddiFormatDate(from, NoddiDateFormats.API_DATE) : undefined;
  const toDate = to ? noddiFormatDate(to, NoddiDateFormats.API_DATE) : undefined;

  useEffect(() => {
    const action = nextForecastPeriodAction({
      periodMode,
      periodSeason,
      fromDate,
      toDate,
      currentSeason,
      selectedSeason
    });
    if (action.kind === 'custom') {
      setPeriod({ 'period-mode': 'custom' });
    } else if (action.kind === 'sync-dates') {
      setPeriod({
        'period-mode': 'season',
        'period-season': action.name,
        'from-date': parseDate(action.dateFrom, NoddiDateFormats.API_DATE),
        'to-date': parseDate(action.dateTo, NoddiDateFormats.API_DATE)
      });
    }
  }, [currentSeason, fromDate, periodMode, periodSeason, selectedSeason, setPeriod, toDate]);

  const selectSeason = (season: SeasonDateRangeWindow) =>
    setPeriod({
      'period-mode': 'season',
      'period-season': season.name,
      'from-date': parseDate(season.dateFrom.slice(0, 10), NoddiDateFormats.API_DATE),
      'to-date': parseDate(season.dateTo.slice(0, 10), NoddiDateFormats.API_DATE)
    });

  return {
    dateMode,
    seasons,
    activeSeason,
    dateRange: from || to ? { from: from ?? undefined, to: to ?? undefined } : undefined,
    error: seasonsError ?? currentSeasonError,
    refetch: () => {
      void Promise.all([refetchSeasons(), refetchCurrentSeason()]);
    },
    selectSeason,
    selectMode: (mode: SeasonDateRangeMode) => {
      const season = selectedSeason ?? currentSeason;
      if (mode === 'season' && season) {
        selectSeason(season);
      } else {
        setPeriod({ 'period-mode': mode });
      }
    },
    setCustomRange: (range: DateRange | undefined) => {
      if (!range?.from || !range.to || range.from <= range.to) {
        setPeriod({ 'period-mode': 'custom', 'from-date': range?.from, 'to-date': range?.to });
      }
    }
  };
};
